import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { Check, X, Car, Bike, Loader2, Smartphone, Lock, ShieldCheck } from "lucide-react";
import { formatFcfa, adminListPaymentCredentials, adminSavePaymentCredentials } from "../../lib/ride";
import { toast } from "sonner";

const STATUS_FILTER = [
  { v: "pending", label: "En attente" },
  { v: "approved", label: "Approuvés" },
  { v: "rejected", label: "Refusés" },
];

export default function AdminRidePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("drivers");
  const [status, setStatus] = useState("pending");
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [busy, setBusy] = useState("");
  const [reviewing, setReviewing] = useState(null);

  useEffect(() => {
    if (user && user !== false && user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  const loadDrivers = async () => {
    const { data } = await api.get(`/ride/admin/drivers?status=${status}`);
    setDrivers(data);
  };
  const loadRides = async () => {
    const { data } = await api.get("/ride/admin/rides");
    setRides(data);
  };
  const loadPricing = async () => {
    const { data } = await api.get("/ride/pricing");
    setPricing(data.pricing);
  };

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    if (tab === "drivers") loadDrivers();
    else if (tab === "rides") loadRides();
    else loadPricing();
  }, [tab, status, user]);

  const openReview = async (id) => {
    const { data } = await api.get(`/ride/admin/drivers/${id}`);
    setReviewing(data);
  };
  const validate = async (id, decision) => {
    setBusy(id);
    try {
      await api.put(`/ride/admin/drivers/${id}/validate`, { status: decision });
      toast.success(decision === "approved" ? "Chauffeur validé" : "Chauffeur refusé");
      setReviewing(null);
      loadDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy("");
    }
  };

  const savePricing = async (vt, payload) => {
    setBusy(vt);
    try {
      await api.put(`/ride/admin/pricing/${vt}`, payload);
      toast.success("Tarif mis à jour");
      loadPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(""); }
  };

  if (!user || user === false) return <div className="text-center py-16 text-brand-inkSoft">Chargement…</div>;
  if (user.role !== "admin") return <div className="text-center py-16 text-red-700">Accès admin requis</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-ride">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink">Kanama Ride — Admin</h1>
        <Link to="/admin" className="text-xs text-brand-inkSoft hover:text-brand-ink">← Admin places</Link>
      </div>

      <div className="flex gap-1 mb-5 border-b border-brand-line overflow-x-auto">
        {[
          { k: "drivers", label: "Chauffeurs" },
          { k: "rides", label: "Courses" },
          { k: "pricing", label: "Tarifs" },
          { k: "payments", label: "Paiements" },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-ride-tab-${t.k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.k ? "border-brand-gold text-brand-ink" : "border-transparent text-brand-inkSoft hover:text-brand-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "drivers" && (
        <>
          <div className="flex gap-2 mb-4">
            {STATUS_FILTER.map(s => (
              <button
                key={s.v}
                onClick={() => setStatus(s.v)}
                data-testid={`admin-driver-filter-${s.v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  status === s.v ? "bg-brand-ink text-white" : "bg-brand-sandlight text-brand-inkSoft hover:bg-brand-line"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {drivers.length === 0 ? (
            <Card className="p-8 text-center text-sm text-brand-inkSoft" data-testid="admin-drivers-empty">Aucun chauffeur dans cette catégorie</Card>
          ) : (
            <div className="space-y-2.5">
              {drivers.map(d => (
                <Card key={d.id} className="rounded-2xl border-brand-line p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-driver-${d.id}`}>
                  <div className="flex items-center gap-3">
                    {d.vehicle_type === "moto" ? <Bike className="w-5 h-5 text-brand-gold" /> : <Car className="w-5 h-5 text-brand-gold" />}
                    <div>
                      <div className="font-heading font-semibold text-brand-ink">{d.name}</div>
                      <div className="text-xs text-brand-inkSoft">{d.phone} · {d.vehicle_plate} · CNI {d.cni_no}</div>
                    </div>
                  </div>
                  <Button onClick={() => openReview(d.id)} variant="outline" className="rounded-full" data-testid={`admin-driver-review-${d.id}`}>Examiner</Button>
                </Card>
              ))}
            </div>
          )}

          {reviewing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setReviewing(null)} data-testid="admin-driver-modal">
              <Card className="rounded-2xl bg-white max-w-2xl w-full max-h-[88vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between mb-3">
                  <h3 className="font-heading text-xl font-semibold text-brand-ink">{reviewing.name}</h3>
                  <button onClick={() => setReviewing(null)} className="p-1"><X className="w-4 h-4" /></button>
                </div>
                <div className="text-sm grid grid-cols-2 gap-2 mb-4">
                  <div><span className="text-brand-inkMuted">Téléphone</span><div className="font-medium">{reviewing.phone}</div></div>
                  <div><span className="text-brand-inkMuted">Véhicule</span><div className="font-medium">{reviewing.vehicle_type}</div></div>
                  <div><span className="text-brand-inkMuted">Plaque</span><div className="font-medium">{reviewing.vehicle_plate}</div></div>
                  <div><span className="text-brand-inkMuted">Couleur · modèle</span><div className="font-medium">{reviewing.vehicle_color || "—"} · {reviewing.vehicle_model || "—"}</div></div>
                  <div><span className="text-brand-inkMuted">CNI</span><div className="font-medium">{reviewing.cni_no}</div></div>
                  <div><span className="text-brand-inkMuted">Permis</span><div className="font-medium">{reviewing.license_no}</div></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                  {Object.entries(reviewing.docs || {}).filter(([_, v]) => v).map(([k, v]) => (
                    <div key={k} className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">{k}</div>
                      <a href={v} target="_blank" rel="noreferrer"><img src={v} alt={k} className="w-full aspect-[4/3] object-cover rounded-lg border border-brand-line" /></a>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => validate(reviewing.id, "rejected")} disabled={busy === reviewing.id} variant="outline" className="rounded-full border-red-200 text-red-700" data-testid="admin-driver-reject">
                    <X className="w-4 h-4 mr-1" /> Refuser
                  </Button>
                  <Button onClick={() => validate(reviewing.id, "approved")} disabled={busy === reviewing.id} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-driver-approve">
                    <Check className="w-4 h-4 mr-1" /> Approuver
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {tab === "rides" && (
        <div className="space-y-2">
          {rides.length === 0 ? <Card className="p-8 text-center text-sm text-brand-inkSoft">Aucune course</Card> :
            rides.map(r => (
              <Card key={r.id} className="rounded-2xl border-brand-line p-3 text-sm grid grid-cols-2 md:grid-cols-5 gap-2" data-testid={`admin-ride-${r.id}`}>
                <div><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Type</div>{r.vehicle_type}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Statut</div>{r.status}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Distance</div>{r.distance_km} km</div>
                <div><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Prix</div>{formatFcfa(r.final_price ?? r.estimated_price)}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Note</div>{r.client_rating ? `${r.client_rating} ★` : "—"}</div>
              </Card>
            ))}
        </div>
      )}

      {tab === "pricing" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pricing.map(p => (
            <Card key={p.vehicle_type} className="rounded-2xl border-brand-line p-4" data-testid={`admin-pricing-${p.vehicle_type}`}>
              <div className="flex items-center gap-2 mb-3">
                {p.vehicle_type === "moto" ? <Bike className="w-5 h-5 text-brand-gold" /> : <Car className="w-5 h-5 text-brand-gold" />}
                <h3 className="font-heading font-semibold text-brand-ink uppercase">{p.vehicle_type}</h3>
              </div>
              <PricingForm cfg={p} busy={busy === p.vehicle_type} onSave={(form) => savePricing(p.vehicle_type, form)} />
            </Card>
          ))}
        </div>
      )}

      {tab === "payments" && <PaymentsTab />}
    </div>
  );
}

function PaymentsTab() {
  const [creds, setCreds] = useState({ airtel: null, moov: null });
  const [busy, setBusy] = useState("");

  const load = async () => {
    const list = await adminListPaymentCredentials();
    const map = { airtel: null, moov: null };
    list.forEach(c => { map[c.provider] = c; });
    setCreds(map);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="admin-payments-banner">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Credentials chiffrés.</strong> Le client_secret et le callback_secret ne sont jamais réaffichés —
          ressaisir pour modifier. URL de callback à configurer côté fournisseur :{" "}
          <code className="bg-white/60 px-1 rounded">{`${window.location.origin}/api/ride/payments/{provider}/callback`}</code>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["airtel", "moov"].map(provider => (
          <Card key={provider} className="rounded-2xl border-brand-line p-4" data-testid={`admin-payments-${provider}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: provider === "airtel" ? "#E60000" : "#003DA5", color: "#fff" }}>
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-brand-ink">{provider === "airtel" ? "Airtel Money" : "Moov Money"}</h3>
                  <div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">{creds[provider]?.environment_mode || "sandbox"}</div>
                </div>
              </div>
              {creds[provider]?.has_secret && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Actif</span>
              )}
            </div>
            <CredForm
              provider={provider}
              current={creds[provider]}
              busy={busy === provider}
              onSave={async (form) => {
                setBusy(provider);
                try {
                  await adminSavePaymentCredentials({ provider, ...form });
                  toast.success("Credentials enregistrés");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erreur");
                } finally { setBusy(""); }
              }}
            />
          </Card>
        ))}
      </div>
    </div>
  );
}

function CredForm({ provider, current, busy, onSave }) {
  const [env, setEnv] = useState(current?.environment_mode || "sandbox");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [callbackSecret, setCallbackSecret] = useState("");
  const idLabel = provider === "airtel" ? "Client ID" : "Merchant ID";
  const secretLabel = provider === "airtel" ? "Client Secret" : "API Key";

  return (
    <div className="space-y-2.5">
      <div>
        <Label className="text-xs">Environnement</Label>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          {["sandbox", "production"].map(e => (
            <button
              key={e}
              type="button"
              onClick={() => setEnv(e)}
              data-testid={`cred-${provider}-env-${e}`}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                env === e ? "border-brand-gold bg-brand-gold/10 text-brand-ink" : "border-brand-line text-brand-inkSoft"
              }`}
            >
              {e === "production" && <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />}
              {e}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">{idLabel}</Label>
        <Input
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder={current?.client_id_masked || "Saisir le " + idLabel}
          className="h-11 rounded-xl text-base"
          data-testid={`cred-${provider}-client-id`}
        />
      </div>
      <div>
        <Label className="text-xs">{secretLabel} <span className="text-brand-inkMuted text-[10px]">(jamais affiché)</span></Label>
        <Input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder={current?.has_secret ? "•••• (modifier)" : "Saisir le " + secretLabel}
          className="h-11 rounded-xl text-base"
          autoComplete="new-password"
          data-testid={`cred-${provider}-client-secret`}
        />
      </div>
      <div>
        <Label className="text-xs">Callback signing secret (HMAC) <span className="text-brand-inkMuted text-[10px]">(optionnel)</span></Label>
        <Input
          type="password"
          value={callbackSecret}
          onChange={(e) => setCallbackSecret(e.target.value)}
          placeholder={current?.has_callback_secret ? "•••• (modifier)" : "Secret HMAC pour vérifier les webhooks"}
          className="h-11 rounded-xl text-base"
          autoComplete="new-password"
          data-testid={`cred-${provider}-callback-secret`}
        />
      </div>
      <Button
        onClick={() => onSave({ environment_mode: env, client_id: clientId, client_secret: clientSecret, callback_secret: callbackSecret })}
        disabled={busy}
        className="w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold"
        data-testid={`cred-${provider}-save`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
      </Button>
    </div>
  );
}

function PricingForm({ cfg, onSave, busy }) {
  const [f, setF] = useState({
    base_fare: cfg.base_fare,
    per_km: cfg.per_km,
    per_min: cfg.per_min,
    minimum_fare: cfg.minimum_fare,
    avg_speed_kmh: cfg.avg_speed_kmh,
  });
  const upd = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  return (
    <div className="space-y-2.5">
      <div><Label className="text-xs">Prise en charge (FCFA)</Label><Input type="number" value={f.base_fare} onChange={upd("base_fare")} className="h-11 rounded-xl text-base" data-testid={`pricing-base-${cfg.vehicle_type}`} /></div>
      <div><Label className="text-xs">FCFA / km</Label><Input type="number" value={f.per_km} onChange={upd("per_km")} className="h-11 rounded-xl text-base" data-testid={`pricing-perkm-${cfg.vehicle_type}`} /></div>
      <div><Label className="text-xs">FCFA / min</Label><Input type="number" value={f.per_min} onChange={upd("per_min")} className="h-11 rounded-xl text-base" data-testid={`pricing-permin-${cfg.vehicle_type}`} /></div>
      <div><Label className="text-xs">Course minimum (FCFA)</Label><Input type="number" value={f.minimum_fare} onChange={upd("minimum_fare")} className="h-11 rounded-xl text-base" data-testid={`pricing-min-${cfg.vehicle_type}`} /></div>
      <div><Label className="text-xs">Vitesse moyenne (km/h)</Label><Input type="number" value={f.avg_speed_kmh} onChange={upd("avg_speed_kmh")} className="h-11 rounded-xl text-base" data-testid={`pricing-speed-${cfg.vehicle_type}`} /></div>
      <Button onClick={() => onSave(f)} disabled={busy} className="w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold" data-testid={`pricing-save-${cfg.vehicle_type}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
      </Button>
    </div>
  );
}
